import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, Evidence } from '../api/client';
import {
  Share2,
  FileText,
  Clock,
  Eye,
  Copy,
  Check,
  Plus,
  ExternalLink,
  Shield,
  Calendar,
  User,
  AlertTriangle,
} from 'lucide-react';

interface DiscoveryShare {
  id: string;
  caseNumber: string;
  evidenceId?: string;
  sharedWith: string;
  sharedWithRole: string;
  shareType: 'View' | 'Download' | 'Full';
  accessUrl?: string;
  accessCode?: string;
  expiresAt?: string;
  sharedBy: string;
  sharedAt: string;
  lastAccessedAt?: string;
  accessCount: number;
  evidenceTitle?: string;
  fileName?: string;
}

function ShareStatusBadge({ share }: { share: DiscoveryShare }) {
  const isExpired = share.expiresAt && new Date(share.expiresAt) < new Date();
  
  if (isExpired) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <Clock className="h-3 w-3" />
        Expired
      </span>
    );
  }
  
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <Check className="h-3 w-3" />
      Active
    </span>
  );
}

function AccessCountBadge({ count }: { count: number }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <Eye className="h-3 w-3" />
      {count} {count === 1 ? 'access' : 'accesses'}
    </span>
  );
}

export default function Discovery() {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [selectedCase, setSelectedCase] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedShare, setSelectedShare] = useState<DiscoveryShare | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  
  // Form state for new share
  const [newShare, setNewShare] = useState({
    evidenceId: '',
    sharedWith: 'Defense',
    sharedWithRole: 'Defense Attorney',
    shareType: 'View' as 'View' | 'Download' | 'Full',
    expiresInDays: 30,
    notes: '',
  });

  // Fetch cases
  const { data: cases } = useQuery({
    queryKey: ['cases'],
    queryFn: () => apiClient.getCases(),
  });

  // Fetch discovery shares for selected case
  const { data: shares, isLoading: sharesLoading } = useQuery({
    queryKey: ['discovery-shares', selectedCase],
    queryFn: () => apiClient.getDiscoveryShares(selectedCase),
    enabled: !!selectedCase,
  });

  // Fetch case evidence for sharing
  const { data: caseEvidence } = useQuery({
    queryKey: ['evidence', selectedCase],
    queryFn: () => apiClient.getCaseEvidence(selectedCase),
    enabled: !!selectedCase,
  });

  // Create share mutation
  const createShareMutation = useMutation({
    mutationFn: (data: {
      caseNumber: string;
      evidenceId?: string;
      sharedWith: string;
      sharedWithRole: string;
      shareType: 'View' | 'Download' | 'Full';
      expiresInDays?: number;
      notes?: string;
    }) => apiClient.createDiscoveryShare(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery-shares', selectedCase] });
      setShowCreateModal(false);
      setNewShare({
        evidenceId: '',
        sharedWith: 'Defense',
        sharedWithRole: 'Defense Attorney',
        shareType: 'View',
        expiresInDays: 30,
        notes: '',
      });
    },
  });

  // Revoke share mutation
  const revokeShareMutation = useMutation({
    mutationFn: (shareId: string) => apiClient.revokeDiscoveryShare(shareId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discovery-shares', selectedCase] });
      setSelectedShare(null);
    },
  });

  const handleCreateShare = () => {
    if (!selectedCase || !newShare.sharedWith || !newShare.sharedWithRole) return;
    
    createShareMutation.mutate({
      caseNumber: selectedCase,
      evidenceId: newShare.evidenceId || undefined,
      sharedWith: newShare.sharedWith,
      sharedWithRole: newShare.sharedWithRole,
      shareType: newShare.shareType,
      expiresInDays: newShare.expiresInDays,
      notes: newShare.notes || undefined,
    });
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDaysUntilExpiry = (expiresAt: string) => {
    const days = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return days;
  };

  // Check role access
  if (!hasRole(['Attorney', 'CaseManager', 'SystemAdmin'])) {
    return (
      <div className="p-8 text-center">
        <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-700">Access Restricted</h2>
        <p className="text-gray-500 mt-2">Only Attorney, Case Manager, and System Admin roles can access Discovery.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-justice-navy">Discovery Management</h1>
          <p className="text-gray-500 mt-1">Share evidence with defense counsel and track compliance</p>
        </div>
      </div>

      {/* Case Selector */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Case
        </label>
        <select
          value={selectedCase}
          onChange={(e) => setSelectedCase(e.target.value)}
          className="w-full md:w-96 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
        >
          <option value="">Select a case...</option>
          {cases?.map((c) => (
            <option key={c.caseNumber} value={c.caseNumber}>
              {c.caseNumber} - {c.title}
            </option>
          ))}
        </select>
      </div>

      {/* Discovery Shares List */}
      {selectedCase && (
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-justice-navy">Discovery Shares</h2>
              <p className="text-sm text-gray-500">Evidence shared with external parties</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-justice-navy text-white rounded-lg hover:bg-justice-navy/90 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Share Evidence
            </button>
          </div>

          {sharesLoading ? (
            <div className="p-8 text-center text-gray-500">Loading shares...</div>
          ) : !shares || shares.length === 0 ? (
            <div className="p-8 text-center">
              <Share2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No evidence has been shared for this case yet.</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 text-justice-gold hover:text-justice-gold/80 font-medium"
              >
                Create your first discovery share
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {shares.map((share: DiscoveryShare) => (
                <div
                  key={share.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedShare(share)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-medium text-justice-navy">{share.sharedWith}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{share.sharedWithRole}</span>
                        <ShareStatusBadge share={share} />
                        <AccessCountBadge count={share.accessCount} />
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {share.shareType} Access
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="h-4 w-4" />
                          {share.evidenceTitle || (share.evidenceId ? `Evidence: ${share.evidenceId.substring(0, 8)}...` : 'All Case Evidence')}
                        </span>
                        {share.expiresAt && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Expires: {formatDate(share.expiresAt)}
                          </span>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Compliance Summary */}
      {selectedCase && shares && shares.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-justice-navy mb-4">Discovery Compliance Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-700">{shares.length}</div>
              <div className="text-sm text-blue-600">Total Shares</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-green-700">
                {shares.filter((s: DiscoveryShare) => !s.expiresAt || new Date(s.expiresAt) > new Date()).length}
              </div>
              <div className="text-sm text-green-600">Active Shares</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-amber-700">
                {shares.reduce((sum: number, s: DiscoveryShare) => sum + s.accessCount, 0)}
              </div>
              <div className="text-sm text-amber-600">Total Accesses</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-700">
                {new Set(shares.map((s: DiscoveryShare) => s.sharedWith)).size}
              </div>
              <div className="text-sm text-purple-600">Recipients</div>
            </div>
          </div>
        </div>
      )}

      {/* Create Share Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-justice-navy">Share Evidence for Discovery</h3>
              <p className="text-sm text-gray-500 mt-1">Create a secure share link for defense counsel</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Evidence Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Evidence to Share
                </label>
                <select
                  value={newShare.evidenceId}
                  onChange={(e) => setNewShare({ ...newShare, evidenceId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
                >
                  <option value="">All case evidence</option>
                  {caseEvidence?.map((ev: Evidence) => (
                    <option key={ev.id} value={ev.id}>
                      {ev.fileName} ({ev.evidenceType})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Leave as "All" to share entire case discovery</p>
              </div>

              {/* Shared With */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Share With (Name/Organization) *
                </label>
                <input
                  type="text"
                  value={newShare.sharedWith}
                  onChange={(e) => setNewShare({ ...newShare, sharedWith: e.target.value })}
                  placeholder="e.g., Smith & Associates Law Firm"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
                />
              </div>

              {/* Shared With Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Role *
                </label>
                <select
                  value={newShare.sharedWithRole}
                  onChange={(e) => setNewShare({ ...newShare, sharedWithRole: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
                >
                  <option value="Defense Attorney">Defense Attorney</option>
                  <option value="Defense Paralegal">Defense Paralegal</option>
                  <option value="Expert Witness">Expert Witness</option>
                  <option value="Co-Counsel">Co-Counsel</option>
                  <option value="Court Staff">Court Staff</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Share Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Level *
                </label>
                <select
                  value={newShare.shareType}
                  onChange={(e) => setNewShare({ ...newShare, shareType: e.target.value as 'View' | 'Download' | 'Full' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
                >
                  <option value="View">View Only - Can view but not download</option>
                  <option value="Download">Download - Can view and download</option>
                  <option value="Full">Full Access - View, download, and request copies</option>
                </select>
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Expires In
                </label>
                <select
                  value={newShare.expiresInDays}
                  onChange={(e) => setNewShare({ ...newShare, expiresInDays: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
                >
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Internal)
                </label>
                <textarea
                  value={newShare.notes}
                  onChange={(e) => setNewShare({ ...newShare, notes: e.target.value })}
                  placeholder="Discovery request reference, special instructions..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-justice-gold focus:border-transparent"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateShare}
                disabled={!newShare.sharedWith || !newShare.sharedWithRole || createShareMutation.isPending}
                className="px-4 py-2 bg-justice-navy text-white rounded-lg hover:bg-justice-navy/90 transition-colors disabled:opacity-50"
              >
                {createShareMutation.isPending ? 'Creating...' : 'Create Share'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Detail Modal */}
      {selectedShare && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-justice-navy">Discovery Share Details</h3>
                <ShareStatusBadge share={selectedShare} />
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Access Code */}
              {selectedShare.accessCode && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Access Code</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 font-mono text-lg bg-white px-3 py-2 rounded border border-gray-200">
                      {selectedShare.accessCode}
                    </code>
                    <button
                      onClick={() => handleCopyCode(selectedShare.accessCode!)}
                      className="p-2 text-gray-500 hover:text-justice-navy transition-colors"
                      title="Copy to clipboard"
                    >
                      {copiedCode === selectedShare.accessCode ? (
                        <Check className="h-5 w-5 text-green-500" />
                      ) : (
                        <Copy className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Share this code securely with the recipient</p>
                </div>
              )}

              {/* Recipient Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Shared With</label>
                  <p className="font-medium text-gray-900">{selectedShare.sharedWith}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Role</label>
                  <p className="font-medium text-gray-900">{selectedShare.sharedWithRole}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Access Level</label>
                  <p className="font-medium text-gray-900">{selectedShare.shareType}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Evidence</label>
                  <p className="font-medium text-gray-900">
                    {selectedShare.evidenceTitle || (selectedShare.evidenceId ? `ID: ${selectedShare.evidenceId.substring(0, 8)}...` : 'All Case Evidence')}
                  </p>
                </div>
              </div>

              {/* Access Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Access Count</label>
                  <p className="font-medium text-gray-900">{selectedShare.accessCount} times</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Last Accessed</label>
                  <p className="font-medium text-gray-900">
                    {selectedShare.lastAccessedAt ? formatDate(selectedShare.lastAccessedAt) : 'Never'}
                  </p>
                </div>
              </div>

              {/* Expiration Warning */}
              {selectedShare.expiresAt && getDaysUntilExpiry(selectedShare.expiresAt) <= 7 && getDaysUntilExpiry(selectedShare.expiresAt) > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <span className="text-sm text-amber-700">
                    Expires in {getDaysUntilExpiry(selectedShare.expiresAt)} days
                  </span>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Shared On</label>
                  <p className="text-gray-900">{formatDate(selectedShare.sharedAt)}</p>
                </div>
                {selectedShare.expiresAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Expires</label>
                    <p className="text-gray-900">{formatDate(selectedShare.expiresAt)}</p>
                  </div>
                )}
              </div>

              {/* Shared By */}
              <div>
                <label className="block text-sm font-medium text-gray-500">Shared By</label>
                <p className="text-gray-900">{selectedShare.sharedBy}</p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-between">
              <button
                onClick={() => setSelectedShare(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Close
              </button>
              {(!selectedShare.expiresAt || new Date(selectedShare.expiresAt) > new Date()) && (
                <button
                  onClick={() => revokeShareMutation.mutate(selectedShare.id)}
                  disabled={revokeShareMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {revokeShareMutation.isPending ? 'Revoking...' : 'Revoke Access'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
